import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {BlogEntryDTO} from "../../../DTO/BlogEntryDTO";
import {ProjectDTO} from "../../../DTO/ProjectDTO";

interface Props {
    entry: BlogEntryDTO;
    onUpdated: (entry: BlogEntryDTO) => void;
    onClose: () => void;
}

const BlogEditForm: React.FC<Props> = ({ entry, onUpdated, onClose }) => {

    const formattedDate = entry.publishDate
        ? new Date(entry.publishDate).toISOString().slice(0, 16)
        : '';

    const [title, setTitle] = useState(entry.title);
    const [slug, setSlug] = useState(entry.slug);
    const [content, setContent] = useState(entry.content);
    const [publishDate, setPublishDate] = useState(formattedDate);
    const [visible, setVisible] = useState(entry.visible);
    const [projectId, setProjectId] = useState<number | ''>(entry.projectId || '');
    const [projects, setProjects] = useState<ProjectDTO[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        axios.get<ProjectDTO[]>('/api/projects')
            .then((res) => setProjects(res.data))
            .catch((err) => console.error('Failed to load projects:', err));
    }, []);

    const handleUpdate = async () => {
        setLoading(true);
        try {
            const payload = {
                title,
                slug,
                content,
                publishDate: publishDate ? new Date(publishDate).toISOString() : null,
                visible,
                projectId: projectId || null
            };

            const { data } = await axios.put<BlogEntryDTO>(`/api/blog/${entry.id}`, payload);
            onUpdated(data);
            onClose();
        } catch (err) {
            console.error('Failed to update blog entry:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-ink/50 flex items-start justify-center pt-20 z-50">
            <div className="bg-surface text-on-surface p-6 rounded shadow-md w-full max-w-2xl border border-border overflow-y-auto max-h-[90vh]">
                <h2 className="text-lg font-semibold mb-4">Edit Blog Post</h2>

                <input
                    type="text"
                    placeholder="Title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="block mb-3 w-full border border-border rounded px-2 py-1 bg-background text-on-surface"
                />
                <input
                    type="text"
                    placeholder="Slug"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    className="block mb-3 w-full border border-border rounded px-2 py-1 bg-background text-on-surface"
                />
                <textarea
                    placeholder="Content (Markdown supported)"
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    className="block mb-3 w-full border border-border rounded px-2 py-1 bg-background text-on-surface h-32"
                />
                <input
                    type="datetime-local"
                    value={publishDate}
                    onChange={(e) => setPublishDate(e.target.value)}
                    className="block mb-3 w-full border border-border rounded px-2 py-1 bg-background text-on-surface"
                />
                <label className="block mb-3">
                    <input
                        type="checkbox"
                        checked={visible}
                        onChange={(e) => setVisible(e.target.checked)}
                        className="mr-2"
                    />
                    Visible
                </label>

                <select
                    value={projectId}
                    onChange={(e) => setProjectId(e.target.value ? +e.target.value : '')}
                    className="block mb-4 w-full border border-border rounded px-2 py-1 bg-background text-on-surface"
                >
                    <option value="">No Project</option>
                    {projects.map((p) => (
                        <option key={p.id} value={p.id}>
                            {p.name}
                        </option>
                    ))}
                </select>

                <div className="flex justify-end space-x-2 mt-4">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded border border-border text-on-surface hover:bg-surface-hover"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleUpdate}
                        disabled={loading}
                        className="btn-primary"
                    >
                        {loading ? 'Saving…' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BlogEditForm;
